import $ from 'jquery';
import { cssAnimation } from '../util/animate-css.js';
import { doFetch } from './request.js';

/**
 * @typedef {{
 *  type?: 'fragment'|'modal-content'|'drawer-content';
 *  url?: string;
 *  method?: string;
 *  data?: any;
 *  ref?: {};
 *  loader?: boolean;
 *  animation?: boolean|string;
 *  contentSelector?: string;
 *  strictContentSelector?: boolean;
 *  resourceSelector?: string;
 *  replaceContainer?: boolean;
 *  append?: boolean;
 *  restoreOriginal?: boolean;
 *  throwOnInvalidStatus?: boolean;
 *  allowDetached?: boolean;
 * }} LoadPartialOptions
 */

/**
 * Custom location error
 */
export class LocationError extends Error {

	static get NAME() {
		return 'LocationError';
	}

	/**
	 * @param {string} location
	 * @param {string} message
	 */
	constructor(location, message) {
		super(message);
		this.location = location;
		// @ts-ignore
		// eslint-disable-next-line unicorn/custom-error-definition
		this.name = this.constructor.NAME;
	}

	toString() {
		/** @type {any} */
		const ctr = this.constructor;
		return `${ctr.NAME} (${this.message}) : ${this.location}`;
	}
}

/**
 * Partial content markup class
 */
export class PartialContent {

	#data;

	/**
	 * @param {{ url: string; content?: JQuery; original?: JQuery<HTMLElement | Text | Comment | Document>; redirect?: string; message?: { format?: 'toast', type?: import('../modules/components/toast').MpToastOptions['format']; content: string; }; title?: string; footer?: string; }} data
	 */
	constructor(data) {
		this.#data = data;
	}

	get url() {
		return this.#data.url;
	}

	get content() {
		return this.#data.content;
	}

	set content(content) {
		if (content && !content.jquery) {
			throw new Error('Content must be a jQuery object');
		}
		this.#data.content = content;
	}

	get original() {
		return this.#data.original;
	}

	set original(content) {
		if (content && !content.jquery) {
			throw new Error('Content must be a jQuery object');
		}
		this.#data.original = content;
	}

	get title() {
		return this.#data.title;
	}

	set title(title) {
		this.#data.title = title;
	}

	get footer() {
		return this.#data.footer;
	}

	set footer(footer) {
		this.#data.footer = footer;
	}

	get redirect() {
		return this.#data.redirect;
	}

	set redirect(url) {
		this.#data.redirect = url;
	}

	get message() {
		return this.#data.message;
	}

	set message(message) {
		this.#data.message = message;
	}
}

/**
 * @param {string} message
 */
export const getErrorMarkup = (message) => {
	return `<div class="container">
		<div class="alert alert--error alert--dismissible" role="alert">
			<div class="alert__message">${message}</div>
		</div>
	</div>`;
};

/**
 * @param {JQuery} $content
 * @param {string=} resourceSelector
 */
export const getAjaxResources = ($content, resourceSelector) => {

	let $contentResources;

	// Get the excluded content
	if (resourceSelector) {

		$contentResources = $content.filter(resourceSelector);
		$contentResources = $contentResources.add($content.find(resourceSelector));
		$contentResources = $contentResources.filter(function() {
			return ($(this).children().length > 0);
		});
	}
	else {
		$contentResources = $();
	}

	return $contentResources;
};

/**
 * @param {JQuery} $content
 * @param {string=} resourceSelector
 */
export const filterAjaxResources = ($content, resourceSelector = '[data-ajax-content-resources]') => {

	let $contentResources = getAjaxResources($content, resourceSelector);
	let $bottomRes;
	let $topRes;

	// Append resources to dom
	if ($contentResources.length > 0) {

		// Avoid adding duplicate js/css to DOM
		$contentResources = dropDuplicateResources($contentResources);

		// Head resources
		$topRes = $contentResources.filter('[data-ajax-placement-top]').detach();
		// Body resources
		$bottomRes = $contentResources.filter('[data-ajax-placement-bottom]').detach();
		// Content resources
		$contentResources = $contentResources.not($topRes).not($bottomRes);
	}
	else {
		$topRes = $();
		$bottomRes = $();
	}

	return { top: $topRes, bottom: $bottomRes, content: $contentResources };
};

/**
 * @param {JQuery} $content
 * @param {{ script?: boolean; link?: boolean; }=} tags
 */
export const dropDuplicateResources = ($content, tags) => {

	if (!tags) {
		tags = { script: true, link: true };
	}

	// Filter css resources that have already been loaded into DOM
	if (tags.link === true) {

		// Get all global link resources with href
		const $allLinks = $('link[href]');

		// Remove resources that were already loaded
		$content.find('link[href]').filter(function() {
			// Note: getAttribute('href') will return the href as is authored, this.href will return the normalized href
			// Note: Use the eval'ed href prop so that both values are normalized
			// @ts-ignore
			return ($allLinks.filter((i, v) => v.href === this.href).length > 0);
		}).remove();
	}

	// Filter js resources that have already been loaded into DOM
	if (tags.script === true) {

		// Get all global script resources with src
		const $allScripts = $('script[src]');

		// Remove resources that were already loaded
		$content.find('script[src]').filter(function() {
			// Note: getAttribute('src') will return the src as is authored, this.src will return the normalized href
			// Note: Use the eval'ed src prop so that both values are normalized
			// @ts-ignore
			return ($allScripts.filter((i, v) => v.src === this.src).length > 0);
		}).remove();
	}

	return $content;
};

/**
 * Load content that can be used to displaying in modals/drawers/tooltips etc
 *
 * @param {JQuery} $remoteContainer
 * @param {LoadPartialOptions=} options
 *
 * @returns {Promise<PartialContent>}
 */
export const loadPartial = async ($remoteContainer, options = {}) => {

	// Default options
	options = { animation: true, loader: true, ...options };

	let remoteData = $remoteContainer.data();
	if (!remoteData) {
		remoteData = {};
		$remoteContainer.data(remoteData);
	}

	/** @type {string} */
	const url = options.url || remoteData.remoteUrl;
	const restoreOriginal = (options.restoreOriginal === true);
	const append = (options.append === true);

	// No url provided
	if (!url) {
		throw new Error('No url provided for loadPartial');
	}

	// The remote container is already busy processing another loadPartial request
	if (remoteData._isLoadingPartial === true) {
		throw new Error('LoadPartialInProgress: The remote container is already busy processing another loadPartial request');
	}
	// Container is not in DOM
	else if (($remoteContainer[0].isConnected === false) && (options.allowDetached !== true)) {
		throw new Error('LoadPartialNotConnected: The remote container is not in DOM');
	}

	let $bottomRes;
	let $content;
	let $original;
	let $topRes;
	const partial = new PartialContent({ url });

	// Add loading animation
	if (options.loader === true) {
		mpro.addContentLoader($remoteContainer);
	}

	try {

		// Add the loading flag
		remoteData._isLoadingPartial = true;

		const contentJson = await doFetch(url, {
			headers: {
				'X-Remote-Signature': (options.type || 'fragment'),
			},
			method: options.method || 'GET',
			data: options.data,
		}, {
			// To provide access to AbortController.abort() method through ref.abort()
			ref: options.ref,
		});

		// Got what is expected
		if (contentJson.content) {

			// Parse the content
			$content = $(contentJson.content);
			// Note: Not a good idea to set the default contentSelector, because the intention is to place the *whole* content in place (even if there are multiple fragments in it)
			const { contentSelector /* = '[data-ajax-main-content]' */ } = options;
			const strictContentSelector = options.strictContentSelector === true;
			const resourceSelector = options.resourceSelector ?? '[data-ajax-content-resources]';
			let foundFilteredContent = false;

			// Filter content
			if (contentSelector) {

				// let $filteredContent = $content.filter(contentSelector).add($content.find(contentSelector));
				let $filteredContent = $content.find(contentSelector);

				if ($filteredContent.length > 0) {

					// No need to look for resources on the whole content
					foundFilteredContent = true;

					// Find extra resources that are left out
					if (resourceSelector) {

						// Make sure the selected content is not in this list
						$filteredContent.detach();

						// Filter out-of-scope scripts/css
						const resources = filterAjaxResources($content, resourceSelector);
						$topRes = resources.top;
						$bottomRes = resources.bottom;

						// Add remaining resources to the content
						if (resources.content.length > 0) {
							// Make sure content resources are
							$filteredContent = resources.content.add($filteredContent);
						}
					}
					// Filter css/js resources that have already been loaded into DOM
					else {
						$filteredContent = dropDuplicateResources($filteredContent);
					}

					$content = $filteredContent;
				}
				// When no content is found, make sure it reflects that
				else if (strictContentSelector === true) {
					// The content is empty
					$content = $filteredContent;
				}
				else {
					console.warn(`No content found for selector: ${contentSelector} in partial content from: ${url}`);
				}
			}

			// Must find and filter ajax resources
			if (foundFilteredContent === false) {

				// Filter out-of-scope scripts/css
				if (resourceSelector) {
					const resources = filterAjaxResources($content, resourceSelector);
					$topRes = resources.top;
					$bottomRes = resources.bottom;
				}
				// Filter css/js resources that have already been loaded into DOM
				else {
					$content = dropDuplicateResources($content);
				}
			}
		}
		// Show toast message
		else if (contentJson.message) {
			partial.message = contentJson.message;
		}
		// Got a location redirect
		else if (contentJson.location) {
			partial.redirect = contentJson.location;
			return partial;
		}
	}
	catch (error) {

		// Rethrow the error
		if (options.throwOnInvalidStatus === true) {
			throw error;
		}

		$content = $(getErrorMarkup(mpro.langvar('error_unknown')));
	}
	// Cleanup
	finally {
		// Remove loading animation
		if (options.loader === true) {
			mpro.removeContentLoader($remoteContainer);
		}

		// Remove the loading flag
		remoteData._isLoadingPartial = false;
	}

	// Make sure mpro.push adds callbacks to queue
	mpro.disableCallbacks();

	let $wrapper;

	// Replace the remoteContainer with new content
	if (options.replaceContainer === true) {

		// Got content back
		if ($content && $content.length > 0) {
			// Add a virtual wrapper (delay appending to DOM)
			$content.wrapAll('<div></div>');
			$wrapper = $remoteContainer;
			$original = restoreOriginal ? $remoteContainer : null;
			$remoteContainer = $content.parent();
		}
		// No content
		else {
			// Save the original contents
			if (restoreOriginal) {
				// Detach so we keep event handlers
				$original = $remoteContainer.contents().detach();
			}
			else {
				// Cleanup the remoteContainer
				$remoteContainer.empty().remove();
			}
		}
	}
	// Append the new content inside the remoteContainer
	else {

		// Add head resources first
		if ($topRes && ($topRes.length > 0)) {
			mpro.get$B().prepend($topRes.children());
			$topRes = null;
		}

		// Save the original contents
		if (restoreOriginal) {
			// Detach so we keep event handlers
			$original = $remoteContainer.contents().detach();
		}
		else if (append === false) {
			$remoteContainer.empty();
		}

		// Append the content
		$remoteContainer.append($content);

		// Add body resources last
		if ($bottomRes && ($bottomRes.length > 0)) {
			mpro.get$B().append($bottomRes.children());
			$bottomRes = null;
		}
	}

	// Enhance the overlayer content
	mpro.initStatics($remoteContainer);

	// Resolve lazy template content after the initStatics call
	mpro.resolveLazyContent($remoteContainer);

	// Call all queued callbacks and disable the queue
	mpro.loadPendingCallbacks();

	// Remove the virtually added wrapper
	if ($wrapper && $wrapper.length > 0) {

		// Add head resources first
		if ($topRes && ($topRes.length > 0)) {
			mpro.get$B().prepend($topRes.children());
			$topRes = null;
		}

		// Remove the div wrapper
		$content.unwrap('div');
		// Now we can append to DOM
		$wrapper.replaceWith($content);

		if (restoreOriginal) {
			// Detach so we keep event handlers
			$remoteContainer.detach();
		}
		else {
			$remoteContainer.remove();
		}

		// This is now the $remoteContainer
		$remoteContainer = $wrapper;

		// Add body resources last
		if ($bottomRes && ($bottomRes.length > 0)) {
			mpro.get$B().append($bottomRes.children());
			$bottomRes = null;
		}
	}

	// Animate the new content
	if ((options.animation === true) && ($remoteContainer.length > 0)) {
		const animationModifiers = (typeof options.animation !== 'boolean') ? options.animation : 'fadeIn';

		// Animate only new content
		if (append === true) {
			for (const elem of $content) {
				if (elem instanceof HTMLElement) {
					cssAnimation(elem, animationModifiers, 'faster');
				}
			}
		}
		// Animate the whole container
		else {
			cssAnimation($remoteContainer[0], animationModifiers, 'faster');
		}
	}

	// Add handler to restore original contents
	if (options.restoreOriginal === true) {

		$content.find('[data-restore-original]').one('click', (e) => {

			// Restore the remoteContainer
			if (options.replaceContainer === true) {

				// Got content back
				if ($content && $content.length > 0) {
					// Replace the whole container
					$remoteContainer.replaceWith($original);
				}
				// No content
				else {
					// Append the content
					$remoteContainer.empty().append($original);
				}
			}
			// Restore the contents from inside the container
			else {
				// Append the content
				$remoteContainer.empty().append($original);
			}

			// Disable all other handlers
			return false;
		});
	}

	// Add content to partial
	partial.content = $content;
	// Add the original (replaced) content
	partial.original = $original;

	return partial;
};
